import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Check,
  X,
  Star,
  Zap,
  Shield,
  Building2,
  ArrowRight,
  Sparkles,
  Crown,
  Users,
  Clock,
  HardDrive,
  FileText,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [featureDisplay, setFeatureDisplay] = useState({});
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [currentPlan, setCurrentPlan] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null);

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchCurrentPlan();
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/entitlements/plans`);
      setPlans(response.data.plans);
      setFeatureDisplay(response.data.feature_display);
    } catch (error) {
      toast.error("Failed to load pricing plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const response = await axios.get(`${API}/entitlements/current`);
      setCurrentPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch current plan");
    }
  };

  const validatePromoCode = async (tier) => {
    if (!promoCode.trim()) return;
    
    try {
      const response = await axios.post(`${API}/entitlements/validate-promo`, {
        code: promoCode,
        plan_tier: tier
      });
      setPromoValidation(response.data);
      if (response.data.valid) {
        toast.success(`Code applied: ${response.data.discount_percent}% off!`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to validate code");
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!user) {
      navigate("/login", { state: { redirectTo: "/pricing", selectedPlan: plan.tier } });
      return;
    }

    if (plan.tier === "enterprise") {
      // Contact sales
      window.location.href = "mailto:sales@mymeetingspal.com?subject=Enterprise%20Plan%20Inquiry";
      return;
    }

    // Navigate to checkout
    navigate(`/checkout?plan=${plan.tier}&billing=${billingCycle}${promoCode ? `&promo=${promoCode}` : ""}`);
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case "standard":
        return <Zap className="w-6 h-6" />;
      case "premium_governance":
        return <Shield className="w-6 h-6" />;
      case "enterprise":
        return <Crown className="w-6 h-6" />;
      default:
        return <Star className="w-6 h-6" />;
    }
  };

  const getPlanGradient = (tier) => {
    switch (tier) {
      case "standard":
        return "from-blue-500 to-blue-600";
      case "premium_governance":
        return "from-purple-500 to-purple-700";
      case "enterprise":
        return "from-amber-500 to-orange-600";
      default:
        return "from-slate-500 to-slate-600";
    }
  };

  const formatPrice = (plan) => {
    if (plan.tier === "enterprise") return "Custom";
    const price = billingCycle === "monthly" ? plan.price_monthly : Math.round(plan.price_yearly / 12);
    return `$${price}`;
  };

  const getAnnualSavings = (plan) => {
    if (plan.tier === "enterprise") return null;
    const monthlyTotal = plan.price_monthly * 12;
    const yearlyTotal = plan.price_yearly;
    const savings = monthlyTotal - yearlyTotal;
    return savings > 0 ? savings : null;
  };

  // Feature categories for display
  const featureCategories = {
    "Core Features": [
      "basic_transcription",
      "minutes_generation",
      "docx_export",
      "pdf_export",
      "basic_action_items",
      "email_notifications"
    ],
    "Governance & Compliance": [
      "minutes_approval_workflow",
      "chair_signing",
      "minutes_lock",
      "governance_linting",
      "export_sign_blocking"
    ],
    "Advanced Features": [
      "versioning",
      "audit_events",
      "access_logs",
      "action_register",
      "carry_forward",
      "evidence_links"
    ],
    "Public Company Modules": [
      "resolutions_voting",
      "declarations_register",
      "retention_policies",
      "legal_holds",
      "immutable_finals",
      "board_pack_export"
    ],
    "Enterprise": [
      "sso_entra",
      "sso_google",
      "rbac_matrix",
      "export_controls",
      "custom_branding"
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-800">MyMeetingsPal</span>
            </div>
            {user ? (
              <Button onClick={() => navigate("/dashboard")} variant="outline">
                Back to Dashboard
              </Button>
            ) : (
              <Button onClick={() => navigate("/login")}>
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-16 pb-12 text-center px-6">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
          Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-purple-600">Governance</span> Plan
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
          Professional corporate minutes and governance tools for companies of all sizes.
          From startups to public companies.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className={`text-sm font-medium ${billingCycle === "monthly" ? "text-slate-800" : "text-slate-500"}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              billingCycle === "yearly" ? "bg-teal-500" : "bg-slate-300"
            }`}
          >
            <div
              className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                billingCycle === "yearly" ? "translate-x-8" : "translate-x-1"
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === "yearly" ? "text-slate-800" : "text-slate-500"}`}>
            Yearly
          </span>
          {billingCycle === "yearly" && (
            <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 rounded-full">
              Save up to 17%
            </span>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, idx) => {
            const isCurrentPlan = currentPlan?.plan_tier === plan.tier;
            const isPopular = plan.tier === "premium_governance";
            
            return (
              <div
                key={plan.tier}
                className={`relative bg-white rounded-3xl border-2 transition-all hover:shadow-xl ${
                  isPopular
                    ? "border-purple-300 shadow-lg shadow-purple-500/10 scale-105 z-10"
                    : "border-slate-200 hover:border-teal-200"
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 bg-gradient-to-r from-purple-500 to-purple-700 text-white text-sm font-semibold rounded-full shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute -top-4 right-4">
                    <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getPlanGradient(plan.tier)} flex items-center justify-center text-white shadow-lg`}>
                      {getPlanIcon(plan.tier)}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-800">{plan.name}</h3>
                      <p className="text-sm text-slate-500">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mt-6 mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-slate-800">{formatPrice(plan)}</span>
                      {plan.tier !== "enterprise" && (
                        <span className="text-slate-500">/month</span>
                      )}
                    </div>
                    {plan.tier !== "enterprise" && billingCycle === "yearly" && (
                      <p className="text-sm text-slate-500 mt-1">
                        ${plan.price_yearly}/year (billed annually)
                      </p>
                    )}
                    {getAnnualSavings(plan) && billingCycle === "yearly" && (
                      <p className="text-sm text-green-600 font-medium mt-1">
                        Save ${getAnnualSavings(plan)}/year
                      </p>
                    )}
                  </div>

                  {/* Limits */}
                  <div className="grid grid-cols-2 gap-3 mb-6 p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {plan.limits.meetings_per_month === -1 ? "Unlimited" : plan.limits.meetings_per_month} meetings/mo
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {plan.limits.audio_hours_per_month === -1 ? "Unlimited" : plan.limits.audio_hours_per_month} audio hrs
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <HardDrive className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {plan.limits.storage_gb === -1 ? "Unlimited" : plan.limits.storage_gb}GB storage
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-600">
                        {plan.limits.users === -1 ? "Unlimited" : plan.limits.users} users
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full h-12 text-base font-semibold ${
                      plan.tier === "enterprise"
                        ? "bg-slate-800 hover:bg-slate-900"
                        : isPopular
                        ? "bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
                        : "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                    } shadow-lg`}
                    disabled={isCurrentPlan}
                    data-testid={`select-${plan.tier}-btn`}
                  >
                    {isCurrentPlan ? (
                      "Current Plan"
                    ) : plan.tier === "enterprise" ? (
                      <>Contact Sales <Mail className="w-4 h-4 ml-2" /></>
                    ) : (
                      <>Get Started <ArrowRight className="w-4 h-4 ml-2" /></>
                    )}
                  </Button>

                  {/* Feature Toggle */}
                  <button
                    onClick={() => setExpandedFeatures({ ...expandedFeatures, [plan.tier]: !expandedFeatures[plan.tier] })}
                    className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1"
                  >
                    {expandedFeatures[plan.tier] ? "Hide" : "Show"} all features
                    {expandedFeatures[plan.tier] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {/* Expanded Features */}
                  {expandedFeatures[plan.tier] && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                      {Object.entries(featureCategories).map(([category, features]) => (
                        <div key={category}>
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                            {category}
                          </h4>
                          <ul className="space-y-2">
                            {features.map((feature) => {
                              const hasFeature = plan.features[feature];
                              return (
                                <li key={feature} className="flex items-center gap-2 text-sm">
                                  {hasFeature ? (
                                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <X className="w-4 h-4 text-slate-300 flex-shrink-0" />
                                  )}
                                  <span className={hasFeature ? "text-slate-700" : "text-slate-400"}>
                                    {featureDisplay[feature] || feature}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="bg-white rounded-2xl border border-slate-200 p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6 text-center">Full Feature Comparison</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-4 px-4 font-medium text-slate-500">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan.tier} className="text-center py-4 px-4 font-semibold text-slate-800">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(featureCategories).map(([category, features]) => (
                  <>
                    <tr key={category} className="bg-slate-50">
                      <td colSpan={4} className="py-3 px-4 font-semibold text-slate-600 text-sm">
                        {category}
                      </td>
                    </tr>
                    {features.map((feature) => (
                      <tr key={feature} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-sm text-slate-700">
                          {featureDisplay[feature] || feature}
                        </td>
                        {plans.map((plan) => (
                          <td key={`${plan.tier}-${feature}`} className="text-center py-3 px-4">
                            {plan.features[feature] ? (
                              <Check className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <X className="w-5 h-5 text-slate-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-center">
          <Crown className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Need Enterprise Features?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Get SSO integration, advanced RBAC, custom retention policies, dedicated support,
            and volume pricing for large organizations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => window.location.href = "mailto:sales@mymeetingspal.com"}
              className="bg-white text-slate-800 hover:bg-slate-100 px-8 h-12"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Sales
            </Button>
            <Button
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700 px-8 h-12"
              onClick={() => window.location.href = "tel:+1-888-555-0123"}
            >
              <Phone className="w-4 h-4 mr-2" />
              Schedule a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            {
              q: "Can I switch plans at any time?",
              a: "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, you'll receive credit for your next billing cycle."
            },
            {
              q: "What happens if I exceed my limits?",
              a: "You'll receive a notification when approaching your limits. If you exceed them, you'll be prompted to upgrade your plan to continue using premium features."
            },
            {
              q: "Is there a free trial?",
              a: "Yes! All new accounts start with a 14-day free trial of the Premium Governance plan. No credit card required to start."
            },
            {
              q: "What payment methods do you accept?",
              a: "We accept all major credit cards (Visa, MasterCard, American Express), and for Enterprise plans, we also offer invoicing with NET-30 terms."
            },
            {
              q: "Can I get a refund?",
              a: "We offer a 30-day money-back guarantee for annual plans. Monthly plans can be cancelled at any time without penalty."
            }
          ].map((faq, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-800 mb-2">{faq.q}</h3>
              <p className="text-slate-600 text-sm">{faq.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} MyMeetingsPal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
